import React, { useState, useMemo } from 'react';
import { Radar, Bar } from 'react-chartjs-2'; // For visualization
import { FaGraduationCap, FaSearch, FaBriefcase, FaUserCheck } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Recommendation Alert
const RecommendationAlert = ({ message, type = 'info' }) => {
  const styles = {
    info: 'bg-blue-100 text-blue-800 border-blue-400',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-400',
    success: 'bg-green-100 text-green-800 border-green-400',
  };
  return (
    <div className={`p-4 border-l-4 rounded-lg shadow-md ${styles[type]} flex items-center mb-6`}>
      <FaGraduationCap className="text-2xl mr-3" />
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

// 2. Department/Role Filter
const GapFilter = ({ onFilterChange }) => {
  const [role, setRole] = useState('Software Engineer');
  const roles = ['Software Engineer', 'HR Manager', 'Sales Lead', 'Financial Analyst'];

  const handleChange = (e) => {
    setRole(e.target.value);
    onFilterChange(e.target.value);
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-md flex justify-between items-center mb-6">
      <h3 className="text-lg font-bold text-gray-700">Analyze Skill Gap By Role</h3>
      <div className="flex items-center space-x-4">
        <FaBriefcase className="text-indigo-500" />
        <select
          value={role}
          onChange={handleChange}
          className="p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out bg-gray-50"
        >
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
    </div>
  );
};

// --- Dummy Data ---
// Skills are rated on a scale of 1 (Low) to 5 (Expert)
const mockSkillData = {
  'Software Engineer': {
    required: [5, 4, 4, 3, 5], // React, Node.js, Cloud, CI/CD, Problem Solving
    current: [4.5, 3.0, 3.5, 2.5, 4.0],
    labels: ['React/Frontend', 'Node.js/Backend', 'Cloud (AWS/Azure)', 'CI/CD Automation', 'Problem Solving'],
    gaps: 2,
    totalEmployees: 45,
  },
  'HR Manager': {
    required: [5, 4, 3, 5, 4], // Compliance, Recruiting, Employee Relations, Payroll, Training
    current: [4.0, 3.5, 4.5, 3.0, 3.5],
    labels: ['Labor Compliance (PF/ESI)', 'Strategic Recruiting', 'Employee Relations', 'Payroll Management', 'L&D Strategy'],
    gaps: 1,
    totalEmployees: 8,
  },
  // ... other roles
};

// --- Main Dashboard Component ---

const SkillGapAnalysis = () => {
  const [roleFilter, setRoleFilter] = useState('Software Engineer');

  const currentRoleData = useMemo(() => {
    return mockSkillData[roleFilter] || mockSkillData['Software Engineer'];
  }, [roleFilter]);

  // Data for the Radar Chart
  const radarChartData = {
    labels: currentRoleData.labels,
    datasets: [
      {
        label: 'Required Level (Goal)',
        data: currentRoleData.required,
        backgroundColor: 'rgba(59, 130, 246, 0.2)', // Blue
        borderColor: '#3B82F6',
        pointBackgroundColor: '#3B82F6',
        borderWidth: 2,
      },
      {
        label: 'Current Average Level',
        data: currentRoleData.current,
        backgroundColor: 'rgba(245, 158, 11, 0.2)', // Orange
        borderColor: '#F59E0B',
        pointBackgroundColor: '#F59E0B',
        borderWidth: 2,
      },
    ],
  };

  // Logic to determine key skill gaps for recommendations
  const skillGaps = currentRoleData.labels.map((label, index) => ({
    skill: label,
    gap: currentRoleData.required[index] - currentRoleData.current[index],
  })).filter(item => item.gap >= 1.0); // Focus on gaps of 1.0 or more

  const getRecommendation = () => {
    if (skillGaps.length === 0) {
      return { message: `Great! The ${roleFilter} team is highly skilled. Maintain current training efforts.`, type: 'success' };
    }
    const largestGap = skillGaps.sort((a, b) => b.gap - a.gap)[0];
    return {
      message: `Critical Gap identified in **${largestGap.skill}** (Gap: ${largestGap.gap.toFixed(1)}). Suggest immediate training for all ${roleFilter}s.`,
      type: 'warning'
    };
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Skill Gap Analysis Dashboard 🧠</h1>
      
      {/* 1. Filter Section */}
      <GapFilter onFilterChange={setRoleFilter} />
      
      {/* 2. Recommendation & Summary */}
      <RecommendationAlert 
        message={getRecommendation().message} 
        type={getRecommendation().type}
      />

      {/* 3. Charts Section - Radar and Bar Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Skill Gap Radar Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Competency Map: {roleFilter}</h2>
          <div className="h-96 w-full p-4">
             <Radar data={radarChartData} options={{ 
                 maintainAspectRatio: false, 
                 scales: { r: { suggestedMin: 0, suggestedMax: 5, ticks: { display: false } } } 
             }} />
          </div>
        </div>
        
        {/* Gap Summary Card */}
        <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Role Summary</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">Employees in Role</p>
                <p className="text-2xl font-bold text-indigo-600">{currentRoleData.totalEmployees}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">Critical Skills Gaps (&ge;1.0)</p>
                <p className="text-2xl font-bold text-red-600">{skillGaps.length}</p>
            </div>
          </div>
          <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Top 3 Gaps:</h3>
              <ul className="space-y-2">
                  {skillGaps.sort((a, b) => b.gap - a.gap).slice(0, 3).map((item, index) => (
                      <li key={index} className="flex justify-between text-sm text-gray-700 bg-red-50 p-2 rounded-md">
                          <span>{item.skill}</span>
                          <span className="font-bold text-red-700">{item.gap.toFixed(1)}</span>
                      </li>
                  ))}
                  {skillGaps.length === 0 && <li className="text-sm text-green-700">No critical gaps detected.</li>}
              </ul>
          </div>
        </div>
      </div>

      {/* 4. Actionable Steps Table */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Training and Recruitment Action Plan</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recommended Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {skillGaps.map((item, index) => (
                <tr key={index} className="hover:bg-blue-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.skill}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">{item.gap.toFixed(1)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.gap >= 1.5 ? 'Urgent Hiring + Mandatory Training' : 'Internal Workshop / Mentorship Program'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800`}>
                      Pending Action
                    </span>
                  </td>
                </tr>
              ))}
              {skillGaps.length === 0 && (
                  <tr>
                      <td colSpan="4" className="text-center py-4 text-green-700 font-semibold">No significant skill gaps found for this role.</td>
                  </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SkillGapAnalysis;